import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faApple, faAndroid } from "@fortawesome/free-brands-svg-icons";

const IOS_URL = "https://apps.apple.com/ng/app/betahome-ng/id6749759648";

const ANDROID_URL =
  "https://play.google.com/store/apps/details?id=ng.betahome.betahome";

const APP_DEEP_LINK = "betahome://";
const APP_DEEP_LINK_DEV = "betahome-dev://";

const DownloadPage = () => {
  const [device, setDevice] = useState("unknown");
  const [redirecting, setRedirecting] = useState(false);
  const [appOpenAttempted, setAppOpenAttempted] = useState(false);

  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || "";

    const isIOS =
      /iPad|iPhone|iPod/i.test(userAgent) ||
      (navigator.platform === "MacIntel" &&
        navigator.maxTouchPoints &&
        navigator.maxTouchPoints > 1);

    const isAndroid = /android/i.test(userAgent);

    const redirectToStore = (platform) => {
      const targetUrl = platform === "ios" ? IOS_URL : ANDROID_URL;
      setDevice(platform);
      setRedirecting(true);

      const timer = setTimeout(() => {
        window.location.href = targetUrl;
      }, 1500);

      return () => clearTimeout(timer);
    };

    const tryDeepLink = () => {
      const params = new URLSearchParams(window.location.search);
      const target = params.get("target");
      const fallbackDeepLink = target
        ? `${APP_DEEP_LINK}${target}`
        : APP_DEEP_LINK;

      const platform = isIOS ? "ios" : "android";
      const appLink = isIOS ? `${APP_DEEP_LINK}vendor` : fallbackDeepLink;
      const devAppLink = isIOS
        ? `${APP_DEEP_LINK_DEV}vendor`
        : fallbackDeepLink;

      const linkToOpen = /dev/i.test(window.location.hostname || "")
        ? devAppLink
        : appLink;

      setDevice(platform);
      setRedirecting(true);
      setAppOpenAttempted(true);

      const hidden = document.createElement("iframe");
      hidden.style.display = "none";
      hidden.src = linkToOpen;
      document.body.appendChild(hidden);

      const timer = setTimeout(() => {
        document.body.removeChild(hidden);
        redirectToStore(platform);
      }, 1200);

      return () => {
        clearTimeout(timer);
        if (document.body.contains(hidden)) {
          document.body.removeChild(hidden);
        }
      };
    };

    if (isIOS) {
      return tryDeepLink();
    }

    if (isAndroid) {
      return tryDeepLink();
    }

    setDevice("unknown");
  }, []);

  const handleIOSDownload = () => {
    window.location.href = IOS_URL;
  };

  const handleAndroidDownload = () => {
    window.location.href = ANDROID_URL;
  };

  return (
    <div className="download-page">
      <div className="download-container">
        <div className="download-logo">
          <img
            src="/logo.png"
            alt="Betahome NG"
            className="logo"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        </div>

        <h1>Download Betahome NG</h1>

        <p className="download-description">
          Get the Betahome NG mobile app and enjoy a faster, easier and better
          experience.
        </p>

        {redirecting && device === "ios" && (
          <div className="redirect-message">
            <div className="loader"></div>
            <p>Opening the app...</p>
            <span>Redirecting you to the App Store...</span>
          </div>
        )}

        {redirecting && device === "android" && (
          <div className="redirect-message">
            <div className="loader"></div>
            <p>Opening the app...</p>
            <span>Redirecting you to Google Play...</span>
          </div>
        )}

        <div className="download-options">
          <button className="store-button" onClick={handleIOSDownload}>
            <FontAwesomeIcon icon={faApple} size="2x" />
            <div className="store-text">
              <small>Download on the</small>
              <strong>App Store</strong>
            </div>
          </button>

          <button className="store-button" onClick={handleAndroidDownload}>
            <FontAwesomeIcon icon={faAndroid} size="2x" />
            <div className="store-text">
              <small>Get it on</small>
              <strong>Google Play</strong>
            </div>
          </button>
        </div>

        {device === "unknown" && (
          <div className="desktop-message">
            <p>You&apos;re viewing this page from a desktop device.</p>
            <p>
              Scan the QR code with your phone to download the Betahome NG app.
            </p>
          </div>
        )}

        <div className="download-footer">
          <span>Betahome NG</span>
          <span>•</span>
          <span>Download the mobile app</span>
        </div>
      </div>

      <style>{`
        * {
          box-sizing: border-box;
        }

        .download-page {
          min-height: 100vh;
          width: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 30px 20px;
          background: #f7f7f2;
          font-family: Arial, Helvetica, sans-serif;
        }

        .download-container {
          width: 100%;
          max-width: 520px;
          background: #ffffff;
          border-radius: 24px;
          padding: 45px 30px;
          text-align: center;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
        }

        .download-logo {
          display: flex;
          justify-content: center;
          margin-bottom: 25px;
        }

        .logo {
          width: 90px;
          height: 90px;
          object-fit: contain;
        }

        h1 {
          margin: 0;
          font-size: 30px;
          font-weight: 800;
          color: #1a1a1a;
        }

        .download-description {
          margin: 15px auto 0;
          max-width: 420px;
          color: #666;
          font-size: 16px;
          line-height: 1.6;
        }

        .redirect-message {
          margin-top: 30px;
          padding: 20px;
          border-radius: 14px;
          background: #f8f8f8;
        }

        .redirect-message p {
          margin: 12px 0 5px;
          font-size: 15px;
          font-weight: 700;
          color: #222;
        }

        .redirect-message span {
          color: #777;
          font-size: 14px;
        }

        .loader {
          width: 35px;
          height: 35px;
          margin: 0 auto;
          border-radius: 50%;
          border: 4px solid #ddd;
          border-top-color: #c0c003;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .download-options {
          display: flex;
          gap: 15px;
          justify-content: center;
          margin-top: 30px;
        }

        .store-button {
          flex: 1;
          min-height: 70px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 12px 16px;
          border: none;
          border-radius: 12px;
          background: #111;
          color: #fff;
          cursor: pointer;
          transition: transform 0.2s ease, opacity 0.2s ease;
        }

        .store-button:hover {
          transform: translateY(-2px);
          opacity: 0.9;
        }

        .store-button:active {
          transform: scale(0.98);
        }

        .store-text {
          display: flex;
          flex-direction: column;
          text-align: left;
        }

        .store-text small {
          font-size: 10px;
          font-weight: 400;
          opacity: 0.8;
        }

        .store-text strong {
          font-size: 17px;
          font-weight: 700;
        }

        .desktop-message {
          margin-top: 30px;
          padding: 18px;
          border-radius: 12px;
          background: #fafafa;
        }

        .desktop-message p {
          margin: 5px 0;
          color: #666;
          font-size: 14px;
          line-height: 1.5;
        }

        .download-footer {
          display: flex;
          justify-content: center;
          gap: 8px;
          margin-top: 35px;
          color: #999;
          font-size: 12px;
        }

        @media (max-width: 500px) {
          .download-page {
            padding: 15px;
          }

          .download-container {
            padding: 35px 20px;
            border-radius: 20px;
          }

          h1 {
            font-size: 25px;
          }

          .download-options {
            flex-direction: column;
          }

          .store-button {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default DownloadPage;
